import React from 'react';
import { Modal } from 'antd';
import { UID_DATATYPE, TIME_DATATYPE, PICTURE_DATATYPE, FILE_DATATYPE } from 'common/constants/datatypes';
import { Attribute, AttributeType } from 'common/constants/types';
import { Dayjs } from 'dayjs';
import { isStringDerivedAttribute, parseAttributesToDisplayTexts } from '../../../utils/displayAttributes';
import { AddedAttributeType, AddedSection } from '../../../types';
import DateEditField from './components/DateEditField';
import SingletonEditField from './components/SingletonEditField';
import NonSingletonEditField from './components/NonSingletonEditField';
import ListableEditField from './components/ListableEditField';
import FileEditField from './components/FileEditField';

const editDerivedAttributeErrorModal = () => Modal.error({
  content: 'Sorry you can\'t modify a derived attribute (attributes with a double dash before and behind them are '
    + 'derived attributes)',
});

export interface Props {
  addedSections: AddedSection[];
  attributeType: AddedAttributeType;
  section: AddedSection;
  sections: AddedSection[];
  showAttributeEndTime: boolean;
  showAttributeStartTime: boolean;
  userAttributeTypes: AddedAttributeType[];
  getAttributes: (attributeTypeName: string) => Attribute[];
  getDateValue: (attributeTypeName: string) => Dayjs | undefined;
  getAddedAttributeValue: (sectionName: string, attributeTypeName: string) => string | undefined;
  renderUIDAttributeEditLink: (section: AddedSection, attributeType: AttributeType) => React.ReactElement;
  setUserAttributeTypes: (userAttributeTypes: AddedAttributeType[]) => void;
  setAddedSections: (addedSections: AddedSection[]) => void;
}

const AttributeTypeEditField = ({
  addedSections,
  attributeType,
  getAddedAttributeValue,
  getAttributes,
  getDateValue,
  renderUIDAttributeEditLink,
  section,
  sections,
  showAttributeEndTime,
  showAttributeStartTime,
  setAddedSections,
  setUserAttributeTypes,
  userAttributeTypes,
}: Props) => {
  const attributesForEdit = getAttributes(attributeType.name);
  const attributeValuesForDisplay = attributesForEdit && parseAttributesToDisplayTexts(attributesForEdit);
  const hasDerivedAttributes = attributeValuesForDisplay
    && attributeValuesForDisplay.filter(isStringDerivedAttribute).length > 0;

  if (attributeType.datatype === UID_DATATYPE) {
    /* TODO: remove this type coercion once renderUIDAttributeEditLink is in Typescript */
    return renderUIDAttributeEditLink(section, attributeType as AttributeType);
  }
  if (attributeType.datatype === TIME_DATATYPE) {
    return (
      <DateEditField
        addedSections={addedSections}
        attributeTypeName={attributeType.name}
        getDateValue={getDateValue}
        section={section}
        setAddedSections={setAddedSections}
        setUserAttributeTypes={setUserAttributeTypes}
        userAttributeTypes={userAttributeTypes}
      />
    );
  }
  if (attributeType.datatype === PICTURE_DATATYPE || attributeType.datatype === FILE_DATATYPE) {
    return (
      <FileEditField
        addedSections={addedSections}
        attributeType={attributeType}
        isImage={attributeType.datatype === PICTURE_DATATYPE}
        section={section}
        setAddedSections={setAddedSections}
        setUserAttributeTypes={setUserAttributeTypes}
        userAttributeTypes={userAttributeTypes}
      />
    );
  }
  if ((!attributeType.listable && attributeType.singleton) || showAttributeStartTime) {
    return (
      <SingletonEditField
        addedSections={addedSections}
        attributeTypeName={attributeType.name}
        attributes={attributesForEdit}
        getAddedAttributeValue={getAddedAttributeValue}
        section={section}
        setAddedSections={setAddedSections}
        setUserAttributeTypes={setUserAttributeTypes}
        showAttributeEndTime={showAttributeEndTime}
        showAttributeStartTime={showAttributeStartTime}
        userAttributeTypes={userAttributeTypes}
      />
    );
  }
  if (!attributeType.listable && !attributeType.singleton) {
    return (
      <NonSingletonEditField
        addedSections={addedSections}
        attributeTypeName={attributeType.name}
        attributeValuesForDisplay={attributeValuesForDisplay}
        attributesForEdit={attributesForEdit}
        editDerivedAttributeErrorModal={editDerivedAttributeErrorModal}
        section={section}
        setAddedSections={setAddedSections}
        setUserAttributeTypes={setUserAttributeTypes}
        userAttributeTypes={userAttributeTypes}
      />
    );
  }
  return (
    <ListableEditField
      attributeType={attributeType}
      attributesForEdit={attributesForEdit}
      hasDerivedAttributes={hasDerivedAttributes}
      section={section}
      sections={sections}
      setUserAttributeTypes={setUserAttributeTypes}
      userAttributeTypes={userAttributeTypes}
    />
  );
};

export default AttributeTypeEditField;
